import { useAppDispatch } from "@/store";
import Accordion from "react-bootstrap/Accordion";
import Spinner from "react-bootstrap/Spinner";
import ProductCardSkeleton from "@/components/product-card/product-card-skeleton";
import {
  fetchCategoryFiltersByIdWizzy,
  fetchLoadMoreCategoryProductsByIdWizzy,
  fetchMoreSearchProductsByWizzy,
  fetchSearchProductBywizzy,
  selectCategory,
} from "@/store/slices/category";
import styles from "@/styles/product_list.module.scss";
import { useRouter } from "next/router";
import React, { Fragment, useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import CatgeoryBreadcrumb from "../breadcrumb/breadcrumb";
import Image from "next/image";
import { combineCategoryFilters } from "@/utils/combineCategoryFilters";
import ProductCardWizzy from "@/components/product-card-wizzy/product-card";
import PaginationCounterWizzy from "../pagination-counter/pagination-counter";
import { S3ImageLoader } from "@/image-loaders/s3-image-loader";

const CategoryProductsListWizzy = () => {
  const categoryState = useSelector(selectCategory);
  console.log("categoryState.data", categoryState.data);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [isSearch, setIsSearch] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const apiFilterQuery = useCallback(() => {
    let filter: any = {
      page: 1,
      productsCount: 20,
      currency: "",
      getAllVariants: "false",
      showOOSProductsInOrder: "false",
      attributeFacetValuesLimit: 20,
      attributes: {},
    };
    if (router.query?.fastDelivery) {
      filter.attributes["fast_delivery"] = ["true"];
    }
    if (router.query?.try_it_online) {
      filter.attributes["try_online"] = ["true"];
    }
    if (router.query?.metal?.length) {
      filter.attributes["purity"] =
        typeof router.query?.metal !== "string"
          ? router.query?.metal
          : [router.query?.metal];
    }
    if (router.query?.occasion?.length) {
      filter.attributes["occasions"] =
        typeof router.query?.occasion !== "string"
          ? router.query?.occasion
          : [router.query?.occasion];
    }
    if (router.query?.priceMin || router.query?.priceMax) {
      filter.sellingPrice = [
        {
          lte: router.query?.priceMax || null,
          gte: router.query?.priceMin || 0,
        },
      ];
    }
    if (router.query?.sort?.length) {
      if (router.query?.sort == "price-asc") {
        filter.sort = [
          {
            field: "sellingPrice",
            order: "asc",
            isForVariation: "false",
            isForParent: "true",
          },
        ];
      } else {
        filter.sort = [
          {
            field: "sellingPrice",
            order: "desc",
            isForParent: "true",
            isForVariation: "false",
          },
          {
            field: "sellingPrice",
            order: "asc",
            isForVariation: "true",
            isForParent: "false",
          },
        ];
      }
    } else {
      if (router.route == "/jewellery") {
        if (router.query?.try_it_online == "yes") {
          filter.sort = [
            { field: "priority:float", order: "asc" },
            { field: "sellingPrice", order: "asc" },
          ];
        } else {
          filter.sort = [
            { field: "relevance", order: "asc" },
            { field: "sellingPrice", order: "asc" },
          ];
        }
      } else {
        filter.sort = [
          { field: "priority:float", order: "asc" },
          { field: "relevance", order: "asc" },
        ];
      }
    }
    return filter;
  }, [router.query, router.route]);

  const loadMore = useCallback(() => {
    if (!categoryState.products || loadingMore) return;
    if (categoryState.products.result?.length >= categoryState.products.total) return;

    setLoadingMore(true);
    const nextPage = +categoryState.products.filters?.page + 1;

    const promise = isSearch
      ? dispatch(
          fetchMoreSearchProductsByWizzy({
            query: {
              page: nextPage,
              search: router.query.q,
              filters: {
                ...apiFilterQuery(),
                q: router.query.q,
                searchedKey: categoryState.products.filters?.searchedKey,
                page: nextPage,
              },
            },
          })
        )
      : categoryState.data
      ? dispatch(
          fetchLoadMoreCategoryProductsByIdWizzy({
            id: categoryState.data.id,
            query: {
              page: nextPage,
              filters: {
                ...apiFilterQuery(),
                categories: [String(categoryState.data?.id)],
                q: null,
                page: nextPage,
              },
            },
          })
        )
      : null;

    if (promise) {
      promise.finally(() => {
        setLoadingMore(false);
      });
    } else {
      setLoadingMore(false);
    }
  }, [
    categoryState.products,
    categoryState.data,
    loadingMore,
    isSearch,
    router.query,
    dispatch,
    apiFilterQuery
  ]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentSentinel = sentinelRef.current;
    if (currentSentinel) {
      observer.observe(currentSentinel);
    }

    return () => {
      if (currentSentinel) {
        observer.unobserve(currentSentinel);
      }
    };
  }, [loadMore]);

  useEffect(() => {
    if (categoryState.data) {
      if (
        !router.query.sort &&
        ["bangle-utsav-collection-2023"].includes(categoryState.data.slug)
      ) {
        router.replace(
          {
            pathname: "/jewellery/category/[category]",
            query: {
              category: categoryState.data?.slug,
              ...combineCategoryFilters({
                ...router.query,
                sort: "price-asc",
              }),
            },
          },
          undefined,
          { shallow: true }
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryState.data, dispatch]);

  useEffect(() => {
    if (router.route == "/jewellery") {
      setIsSearch(true);
      if (
        router.query?.fastDelivery ||
        router.query?.try_it_online ||
        router.query?.sort ||
        router.query?.occasion ||
        router.query?.metal ||
        router.query?.priceMax ||
        router.query?.priceMin
      ) {
        dispatch(
          fetchCategoryFiltersByIdWizzy({
            query: {
              search: router.query.q,
              tryItOnline: router.query.try_it_online,
              filters: {
                ...apiFilterQuery(),
                search: router.query.q,
                q: router.query.q,
                searchedKey: categoryState.products?.filters?.searchedKey,
                includeCategoriesFacetForPage: "true",
                facets: [
                  { key: "purity" },
                  { key: "occasions" },
                  { key: "categories" },
                ],
              },
            },
          })
        );
      } else {
        dispatch(
          fetchSearchProductBywizzy({
            query: {
              ...apiFilterQuery(),
              search: router.query.q,
              q: router.query.q,

              searchedKey: categoryState.products?.filters?.searchedKey,
            },
          })
        );
      }
    } else {
      if (categoryState.data) {
        if (
          !router.query.sort &&
          ["bangle-utsav-collection-2023"].includes(categoryState.data.slug)
        ) {
          router.replace(
            {
              pathname: "/jewellery/category/[category]",
              query: {
                category: categoryState.data?.slug,
                ...combineCategoryFilters({
                  ...router.query,
                  sort: "price-asc",
                }),
              },
            },
            undefined,
            { shallow: true }
          );
        } else {
          dispatch(
            fetchCategoryFiltersByIdWizzy({
              query: {
                tryItOnline: router.query.try_it_online,
                filters: {
                  ...apiFilterQuery(),
                  categories: [String(categoryState.data?.id)],
                  includeCategoriesFacetForPage: "true",
                  facets: [
                    { key: "purity" },
                    { key: "occasions" },
                    { key: "categories" },
                  ],
                },
              },
            })
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    router.query?.fastDelivery,
    router.query?.try_it_online,
    router.query?.sort,
    router.query?.occasion,
    router.query?.metal,
    router.query?.priceMax,
    router.query?.priceMin,
    router.query.q,
    router.query.category,
  ]);

  return (
    <div className="container">
      <div className="row g-3">
        {categoryState.productsLoading &&
          new Array(8).fill(1).map((_, k) => (
            <div className="col-md-3" key={k}>
              <ProductCardSkeleton />
            </div>
          ))}
        {categoryState.products?.result &&
          categoryState.products.result.map((product: any, index: any) => (
            <Fragment key={product.id}>
              {index == 10 && categoryState.data?.inner_banner_1 && (
                <div className="col-md-12 col-lg-6 col-12">
                  <div
                    className={`${styles.position_relative} ${styles.w_100} ${styles.custom_height_list} ${styles.h_100}`}
                  >
                    <Image
                      src={categoryState.data?.inner_banner_1}
                      layout="fill"
                      alt="Inner banner 1"
                      priority
                      width={696}
                      height={589}
                      sizes="(max-width: 479px) 84px, (max-width: 640px) 129px, (max-width: 991px) 318px, (max-width: 1199px) 318px, (max-width: 1399px) 696px"
                      loader={S3ImageLoader}
                    />
                  </div>
                </div>
              )}
              {index == 18 && categoryState.data?.inner_banner_2 && (
                <div className="col-md-12 col-lg-6 col-12">
                  <div
                    className={`${styles.position_relative} ${styles.w_100} ${styles.custom_height_list} ${styles.h_100}`}
                  >
                    <Image
                      src={categoryState.data?.inner_banner_2}
                      layout="fill"
                      alt="Inner banner 1"
                      priority
                      width={696}
                      height={589}
                      sizes="(max-width: 479px) 84px, (max-width: 640px) 129px, (max-width: 991px) 318px, (max-width: 1199px) 318px, (max-width: 1399px) 696px"
                      loader={S3ImageLoader}
                    />
                  </div>
                </div>
              )}
              {index == 30 && categoryState.data?.inner_banner_3 && (
                <div className="col-md-12 col-lg-6 col-12">
                  <div
                    className={`${styles.position_relative} ${styles.w_100} ${styles.custom_height_list} ${styles.h_100}`}
                  >
                    <Image
                      src={categoryState.data?.inner_banner_3}
                      layout="fill"
                      alt="Inner banner 1"
                      priority
                      width={696}
                      height={589}
                      sizes="(max-width: 479px) 84px, (max-width: 640px) 129px, (max-width: 991px) 318px, (max-width: 1199px) 318px, (max-width: 1399px) 696px"
                      loader={S3ImageLoader}
                    />
                  </div>
                </div>
              )}
              {index == 38 && categoryState.data?.inner_banner_4 && (
                <div className="col-md-12 col-lg-6 col-12">
                  <div
                    className={`${styles.position_relative} ${styles.w_100} ${styles.custom_height_list} ${styles.h_100}`}
                  >
                    <Image
                      src={categoryState.data?.inner_banner_4}
                      layout="fill"
                      alt="Inner banner 1"
                      priority
                      width={696}
                      height={589}
                      sizes="(max-width: 479px) 84px, (max-width: 640px) 129px, (max-width: 991px) 318px, (max-width: 1199px) 318px, (max-width: 1399px) 696px"
                      loader={S3ImageLoader}
                    />
                  </div>
                </div>
              )}
              <div className="col-md-6 col-lg-3 col-6">
                <ProductCardWizzy product={product} />
              </div>
            </Fragment>
          ))}
        {!categoryState.products?.result?.length &&
          !categoryState.productsLoading && (
            <h1 className="text-center">No records found</h1>
          )}
      </div>
      {categoryState.products &&
        categoryState.products?.result?.length < categoryState.products?.total && (
          <div ref={sentinelRef} style={{ height: "40px", margin: "20px 0", display: "flex", justifyContent: "center", alignItems: "center" }}>
            {loadingMore && (
              <Spinner
                animation="border"
                role="status"
                style={{ color: "rgb(143, 0, 6)" }}
              >
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            )}
          </div>
        )}
      <div className={styles.show_contant}>
        <p className="text-center">
          <PaginationCounterWizzy />
        </p>
        <div className="row mb-4 mt-4 ">
          <div className="col-md-4">
            <div className={styles.breadcrumb_main}>
              {!isSearch && <CatgeoryBreadcrumb />}
            </div>
          </div>
          <div className="col-md-4"></div>
          <div className="col-md-4 text-md-end text-center mt-3 mt-md-0">
            {/* <a href="#" className="text-md-end">
              BACK TO TOP
            </a> */}
          </div>
        </div>
        {!!categoryState.data?.content && (
          <>
            <div className={styles.product_list_contant}>
              {!!categoryState.data.content.title && (
                <div className={styles.product_heading_text}>
                  {categoryState.data.content.title}
                </div>
              )}
              {!!categoryState.data.content.description && (
                <div
                  className={styles.product_bottomg_text}
                  dangerouslySetInnerHTML={{
                    __html: categoryState.data.content.description
                      .replace(/nofollow/g, "")
                      .replace(/noopener/g, "")
                      .replace(/noreferrer/g, ""),
                  }}
                ></div>
              )}
            </div>
            {!!categoryState.data.content.items?.length && (
              <div className={styles.accordion_container}>
                <Accordion defaultActiveKey="0">
                  {categoryState.data.content.items.map((item, index) => (
                    <Accordion.Item eventKey={`${index}`} key={index}>
                      <Accordion.Header>{item.title}</Accordion.Header>
                      <Accordion.Body
                        dangerouslySetInnerHTML={{
                          __html: item.description
                            .replace(/nofollow/g, "")
                            .replace(/noopener/g, "")
                            .replace(/noreferrer/g, ""),
                        }}
                      ></Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CategoryProductsListWizzy;
